import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import type { JsonSchema } from '@shared/types'
import { pluralize } from '@shared/utils'
import type { FieldMeta } from '@tanstack/react-form'
import type { ReactNode } from 'react'
import type { McpAction } from 'v2/pages/settings/types'
import { z } from 'zod'
import type { AiActionNodeData } from '../../../types'
import { ActionField } from './action-fields'

// Generate field configs for FieldsRenderer
export const generateActionFieldConfigs = (
  dynamicFields: {
    required: string[]
    optional: string[]
    schema?: McpAction['input_schema'] | JsonSchema
  },
  nodeVars: string[],
  arrayIndex: number,
  headerComponent?: ReactNode,
): GenericFieldConfig<AiActionNodeData['config']> => {
  const { required: requiredFields, optional: optionalFields, schema } = dynamicFields

  const configs: GenericFieldConfig<AiActionNodeData['config']> = []
  let showOptionalFields = false
  const setShowOptionalFields = (show: boolean) => {
    showOptionalFields = show
  }

  // Add header with refresh functionality if provided
  if (headerComponent) {
    configs.push({
      name: `actions[${arrayIndex}].action_inputs_header`,
      type: 'slot',
      component: headerComponent,
    })
  }

  // Add required fields
  requiredFields.forEach((field) => {
    configs.push({
      name: `actions[${arrayIndex}].action_inputs.${field}`,
      type: 'custom',
      className: 'pl-8',
      onChangeListenTo: [
        `actions[${arrayIndex}].action_field_types.required_fields.${field}`,
        `actions[${arrayIndex}].action_inputs.${field}`,
      ],
      component: ({ form }) => {
        const fieldProperty = schema?.properties?.[field] as
          | { description?: string; type?: string; default?: unknown }
          | undefined

        return (
          <ActionField
            fieldConfig={{
              name: field,
              type: fieldProperty?.type || 'string',
              description: fieldProperty?.description,
              isOptional: false,
              default: fieldProperty?.default,
            }}
            formContext={{
              form,
              arrayIndex,
              allNodeVars: nodeVars,
            }}
          />
        )
      },
      zodValidation: (values) => {
        const actionFieldTypes = values.actions?.[arrayIndex]?.action_field_types
        const fieldType = actionFieldTypes?.required_fields?.[field] || 'auto'

        // Only validate when field type is manual
        if (fieldType === 'manual') {
          return z
            .string({ invalid_type_error: 'This field is required' })
            .min(1, { message: 'This field is required' })
        }

        // For auto fields, allow any value (including null/empty)
        return z.any()
      },
      eventDataExtractor: (data) => data,
      visible: () => true,
    })
  })

  // Add optional fields toggle if there are optional fields
  if (optionalFields.length > 0) {
    configs.push({
      name: `actions[${arrayIndex}].show_optional_fields_toggle`,
      type: 'basic',
      component: ({ form }) => {
        if (showOptionalFields) return null

        return (
          <div className="relative mb-2 mt-1 pl-8">
            <div
              className="absolute left-0 top-4 w-full h-0.5 border-t-2 border-dotted border-st1"
              style={{ width: '1.3rem', left: '0.7rem' }}
              aria-hidden="true"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-primary p-0 pl-1 underline hover:bg-transparent"
              onClick={() => {
                setShowOptionalFields(true)
                optionalFields.forEach((field) => {
                  const currentValue = form.getFieldValue(
                    `actions[${arrayIndex}].action_inputs.${field}`,
                  )
                  if (!currentValue) {
                    form.setFieldValue(`actions[${arrayIndex}].action_inputs.${field}`, '')
                  }
                  form.setFieldMeta(
                    `actions[${arrayIndex}].action_inputs.${field}`,
                    (prev: FieldMeta) => ({
                      ...prev,
                      isTouched: true,
                    }),
                  )
                })
                form.validateAllFields('change')
              }}
              data-testid="cta-show-optional-fields"
            >
              Show {pluralize(optionalFields.length, 'more optional field')}
            </Button>
          </div>
        )
      },
    })
  }

  // Add optional fields
  optionalFields.forEach((field) => {
    configs.push({
      name: `actions[${arrayIndex}].action_inputs.${field}_optional`,
      type: 'custom',
      className: 'pl-8',
      onChangeListenTo: [
        `actions[${arrayIndex}].action_field_types.optional_fields.${field}`,
        `actions[${arrayIndex}].action_inputs.${field}`,
      ],
      component: ({ form }) => {
        if (!showOptionalFields) return null

        const fieldProperty = schema?.properties?.[field] as
          | { description?: string; type?: string; default?: unknown }
          | undefined

        return (
          <ActionField
            fieldConfig={{
              name: field,
              type: fieldProperty?.type || 'string',
              description: fieldProperty?.description,
              isOptional: true,
              default: fieldProperty?.default,
            }}
            formContext={{
              form,
              arrayIndex,
              allNodeVars: nodeVars,
            }}
          />
        )
      },
      // Optional fields are never required, regardless of field type
      zodValidation: z.any(),
      eventDataExtractor: (data) => data,
      visible: () => true,
    })
  })

  // Add hide optional fields button
  if (optionalFields.length > 0) {
    configs.push({
      name: `actions[${arrayIndex}].hide_optional_fields_button`,
      type: 'basic',
      component: ({ form }) => {
        if (!showOptionalFields) return null

        return (
          <div className="relative pl-8 mb-4 mt-1">
            <div
              className="absolute left-0 top-4 w-full h-0.5 border-t-2 border-dotted border-st1"
              style={{ width: '1.3rem', left: '0.7rem' }}
              aria-hidden="true"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-primary underline p-0 pl-1 hover:bg-transparent"
              onClick={() => {
                setShowOptionalFields(false)
                // Force validation to trigger re-render
                form.validateAllFields('change')
              }}
              data-testid="cta-hide-optional-fields"
            >
              Show less
            </Button>
          </div>
        )
      },
    })
  }

  return configs
}
